import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { auditContract, getContractAuditChains, getRecentContractAudits } from '../api';
import { useIsMobile } from '../hooks/useIsMobile';

const CHAIN_LABEL = {
  ethereum: 'Ethereum', bsc: 'BNB Chain', bnb: 'BNB Chain', polygon: 'Polygon',
  arbitrum: 'Arbitrum', optimism: 'Optimism', base: 'Base', avalanche: 'Avalanche', solana: 'Solana', near: 'NEAR',
};
const CHAIN_OPTIONS = ['ethereum', 'bsc', 'polygon', 'arbitrum', 'optimism', 'base', 'avalanche', 'solana', 'near'];

// Labels come from the `audit.level.*`, `audit.trustLevel.*` and
// `audit.category.*` translation keys.
const LEVEL_META = {
  low:      { color: '#10b981', emoji: '🟢' },
  medium:   { color: '#f59e0b', emoji: '🟡' },
  high:     { color: '#dc2626', emoji: '🔴' },
  critical: { color: '#dc2626', emoji: '🔴' },
};

const TRUST_LEVEL_META = {
  unrated: { color: 'var(--text-dim)', emoji: '⚪' },
  low:     { color: '#f59e0b', emoji: '🟡' },
  medium:  { color: '#3b82f6', emoji: '🔵' },
  high:    { color: '#10b981', emoji: '🟢' },
};

const CATEGORY_ORDER = { critical: 0, ownership: 1, trading: 2, liquidity: 3, trust: 4 };

// Factors carry English `label`/`detail` plus Vietnamese `labelVi`/`detailVi`
// (older stored audits may only have a Vietnamese `detail`).
const isVi = (i18n) => (i18n.language || 'vi').startsWith('vi');
const factorLabel = (f, i18n) => (isVi(i18n) ? f.labelVi || f.label : f.label || f.labelVi);
const factorDetail = (f, i18n) => (isVi(i18n) ? f.detailVi || f.detail : f.detail || f.detailVi);

function shortAddr(a) {
  if (!a) return '—';
  return a.length > 16 ? `${a.slice(0, 8)}…${a.slice(-6)}` : a;
}

export default function ContractAuditPage() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [chains, setChains] = useState(CHAIN_OPTIONS);
  const [chain, setChain] = useState('ethereum');
  const [addressInput, setAddressInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    getContractAuditChains().then((r) => { if (r.chains?.length) setChains(r.chains.filter((c) => c !== 'bnb')); }).catch(() => {});
  }, []);

  const loadRecent = useCallback(() => {
    getRecentContractAudits(15).then((r) => setRecent(r.docs || [])).catch(() => {});
  }, []);
  useEffect(() => { loadRecent(); }, [loadRecent]);

  const runAudit = async (chainVal, addressVal) => {
    const addr = addressVal.trim();
    if (!addr) { setError(t('audit.err.missing_address')); return; }
    setLoading(true);
    setError('');
    try {
      const r = await auditContract(chainVal, addr);
      setResult(r);
      loadRecent();
    } catch (err) {
      setError(t(`audit.err.${err.code || 'generic'}`, { defaultValue: t('audit.err.generic') }));
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const pageStyle = isMobile ? { ...s.page, padding: '16px 12px' } : s.page;

  return (
    <div style={pageStyle}>
      <div style={s.header}>
        <h2 style={s.title}>{t('audit.title')}</h2>
        <p style={s.subtitle}>{t('audit.subtitle')}</p>
      </div>

      <div style={s.notice}>{t('audit.notice')}</div>

      <div style={s.form}>
        <div style={s.field}>
          <span style={s.fieldLabel}>{t('audit.network')}</span>
          <select style={s.input} value={chain} onChange={(e) => setChain(e.target.value)}>
            {chains.map((c) => <option key={c} value={c}>{CHAIN_LABEL[c] || c}</option>)}
          </select>
        </div>
        <div style={{ ...s.field, flex: 2, minWidth: 260 }}>
          <span style={s.fieldLabel}>{t('audit.addressLabel')}</span>
          <input
            style={s.input}
            value={addressInput}
            onChange={(e) => setAddressInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') runAudit(chain, addressInput); }}
            placeholder={chain === 'solana' ? t('audit.phSolana') : chain === 'near' ? t('audit.phNear') : '0x...'}
          />
        </div>
        <button style={s.btnPrimary} onClick={() => runAudit(chain, addressInput)} disabled={loading}>
          {loading ? t('audit.checking') : t('audit.check')}
        </button>
      </div>

      {error && <div style={s.errorBox}>{error}</div>}

      {/* Keyed per result: browser auto-translation otherwise keeps showing the
          previous token's translated numbers after React updates the text. */}
      {result && <ResultPanel key={`${result.chain}:${result.address}:${result.fetchedAt}`} result={result} />}

      {recent.length > 0 && (
        <div style={s.recentSection}>
          <div style={s.recentTitle}>{t('audit.recent')}</div>
          <div style={s.recentList}>
            {recent.map((r) => {
              const lvl = LEVEL_META[r.risk?.level];
              return (
                <button
                  key={`${r.chain}:${r.address}`}
                  style={s.recentChip}
                  onClick={() => { setChain(r.chain); setAddressInput(r.address); runAudit(r.chain, r.address); }}
                >
                  <span style={{ fontWeight: 700, color: 'var(--text)' }}>{r.tokenSymbol || shortAddr(r.address)}</span>
                  <span style={{ color: 'var(--text-dim)', fontSize: 10 }}>{CHAIN_LABEL[r.chain] || r.chain}{r.kind === 'contract' ? ` · ${t('audit.contractTag')}` : ''}</span>
                  {lvl && <span style={{ ...s.recentBadge, color: lvl.color, borderColor: lvl.color + '55', background: lvl.color + '14' }}>{lvl.emoji} {r.risk.score}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ResultPanel({ result }) {
  const { t } = useTranslation();
  const { chain, address, kind, tokenName, tokenSymbol, risk, trust, addressSecurity, notFound } = result;

  if (notFound) {
    return (
      <div style={s.resultCard}>
        <div style={s.notFoundBox}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>❓</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>{t('audit.notFoundTitle')}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {kind === 'contract' ? t('audit.notFoundContract') : t('audit.notFoundToken')}
          </div>
        </div>
      </div>
    );
  }

  const lvl = LEVEL_META[risk?.level] || LEVEL_META.medium;
  const trustLvl = TRUST_LEVEL_META[trust?.level] || TRUST_LEVEL_META.unrated;
  const factors = risk?.factors || [];
  const hitRiskCount = factors.filter((f) => f.hit && f.weight > 0).length;
  const passedCount = factors.filter((f) => !f.hit && f.weight > 0).length;

  // Group into category sections, ordered by severity — each section shows
  // EVERY criterion checked in that category (hit or not), not just the
  // ones that tripped, so this reads as a full checklist rather than only
  // a list of complaints.
  const byCategory = new Map();
  for (const f of factors) {
    if (!byCategory.has(f.category)) byCategory.set(f.category, []);
    byCategory.get(f.category).push(f);
  }
  const categories = [...byCategory.keys()].sort((a, b) => (CATEGORY_ORDER[a] ?? 9) - (CATEGORY_ORDER[b] ?? 9));

  const addressFlags = addressSecurity
    ? Object.entries(addressSecurity).filter(([k, v]) => k !== 'contract_address' && (v === '1' || v === 1))
    : [];

  return (
    <div style={s.resultCard}>
      <div style={s.resultHeader}>
        <div>
          <div style={s.resultName}>{kind === 'contract' ? t('audit.smartContract') : (tokenName || t('audit.unknownName'))} {tokenSymbol && <span style={s.resultSymbol}>({tokenSymbol})</span>}</div>
          <div style={s.resultAddr} title={address}>{CHAIN_LABEL[chain] || chain} · {shortAddr(address)}</div>
        </div>
        <div style={s.badgeGroup}>
          <div style={{ ...s.scoreBadge, color: lvl.color, borderColor: lvl.color + '55', background: lvl.color + '14' }}>
            {lvl.emoji} {t(`audit.level.${risk?.level || 'medium'}`)} — {risk?.score ?? '—'}/100
          </div>
          <div style={{ ...s.scoreBadge, color: trustLvl.color, borderColor: trustLvl.color + '55', background: trustLvl.color + '14' }}>
            {trustLvl.emoji} {t(`audit.trustLevel.${trust?.level || 'unrated'}`)} — {trust?.score ?? '—'}/100
          </div>
        </div>
      </div>

      <div style={s.scoreBarsRow}>
        <div style={s.scoreBarCol}>
          <span style={s.scoreBarLabel}>{t('audit.riskScore')}</span>
          <div style={s.scoreBarTrack}>
            <div style={{ ...s.scoreBarFill, width: `${risk?.score ?? 0}%`, background: lvl.color }} />
          </div>
        </div>
        <div style={s.scoreBarCol}>
          <span style={s.scoreBarLabel}>
            {t('audit.trustScore')}{trust?.hygienePct != null && ` · ${t('audit.hygiene', { pct: trust.hygienePct })}`}
          </span>
          <div style={s.scoreBarTrack}>
            <div style={{ ...s.scoreBarFill, width: `${trust?.score ?? 0}%`, background: trustLvl.color }} />
          </div>
        </div>
      </div>

      <div style={s.summaryRow}>
        <span><Trans i18nKey="audit.checked" values={{ count: factors.length }} components={{ 1: <strong /> }} /></span>
        <span style={{ color: hitRiskCount > 0 ? '#dc2626' : 'var(--text-dim)' }}>{t('audit.riskFindings', { count: hitRiskCount })}</span>
        <span style={{ color: '#10b981' }}>{t('audit.passed', { count: passedCount })}</span>
      </div>

      <FindingsBox factors={factors} />

      {addressFlags.length > 0 && (
        <div style={s.addressWarnBox}>
          {t('audit.addressFlags', { flags: addressFlags.map(([k]) => k.replace(/_/g, ' ')).join(', ') })}
        </div>
      )}

      {categories.map((cat) => (
        <div key={cat} style={s.factorSection}>
          <div style={s.factorSectionTitle}>{t(`audit.category.${cat}`, { defaultValue: cat })}</div>
          {byCategory.get(cat).map((f) => (
            <FactorRow key={f.key} f={f} />
          ))}
        </div>
      ))}

      <p style={s.disclaimer}>{t('audit.disclaimer')}</p>
    </div>
  );
}

const severityColor = (w) => (w >= 12 ? '#dc2626' : '#f59e0b');
const scrollToFactor = (key) => document.getElementById(`factor-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });

// Every flagged item (and informational note) up front, so the findings are
// visible without scanning the full checklist.
function FindingsBox({ factors }) {
  const { t, i18n } = useTranslation();
  const risks = factors.filter((f) => f.hit && f.weight > 0).sort((a, b) => b.weight - a.weight);
  const notes = factors.filter((f) => f.hit && f.weight === 0);
  if (!risks.length && !notes.length) {
    return <div style={{ ...s.findingsBox, borderColor: '#10b98155', background: '#10b98110', color: '#059669' }}>{t('audit.noRisks')}</div>;
  }
  const tone = risks.length ? severityColor(risks[0].weight) : 'var(--text-dim)';
  const text = (f) => { const d = factorDetail(f, i18n); return <span style={s.findingText}>{factorLabel(f, i18n)}{d && <span style={s.findingDetail}> — {d}</span>}</span>; };
  return (
    <div style={{ ...s.findingsBox, borderColor: risks.length ? tone + '40' : 'var(--border)', background: risks.length ? tone + '0a' : 'var(--surface)' }}>
      {risks.length > 0 && <div style={{ ...s.findingsTitle, color: tone }}>{t('audit.findingsTitle', { count: risks.length })}</div>}
      {risks.map((f) => (
        <button key={f.key} type="button" style={s.findingItem} onClick={() => scrollToFactor(f.key)}>
          <span style={{ ...s.findingPoints, color: severityColor(f.weight), borderColor: severityColor(f.weight) + '55' }}>+{f.weight}</span>
          {text(f)}
        </button>
      ))}
      {notes.length > 0 && <div style={{ ...s.findingsTitle, marginTop: risks.length ? 8 : 0, color: 'var(--text-muted)' }}>{t('audit.notesTitle')}</div>}
      {notes.map((f) => (
        <button key={f.key} type="button" style={s.findingItem} onClick={() => scrollToFactor(f.key)}>
          <span style={{ ...s.findingPoints, color: 'var(--text-dim)', borderColor: 'var(--border)' }}>i</span>
          {text(f)}
        </button>
      ))}
    </div>
  );
}

function FactorRow({ f }) {
  const { i18n } = useTranslation();
  const detail = factorDetail(f, i18n);
  const isTrust = f.weight < 0;
  // Risk item, hit -> red flag (icon + points added).
  // Risk item, not hit -> passed check (points would-have-been-added shown as 0/greyed).
  // Trust item, hit -> positive signal (points subtracted, shown green).
  // Trust item, not hit -> no signal found (neutral, no points).
  let icon, pointsLabel, pointsColor;
  if (f.weight === 0) {
    // Informational note: adds no risk, shown only when it applies.
    icon = f.hit ? 'ℹ️' : '⚪';
    pointsLabel = '—';
    pointsColor = 'var(--text-dim)';
  } else if (isTrust) {
    icon = f.hit ? '✅' : '⚪';
    pointsLabel = f.hit ? `${f.weight}` : '0';
    pointsColor = f.hit ? '#10b981' : 'var(--text-dim)';
  } else {
    icon = f.hit ? (f.weight >= 12 ? '🔴' : '🟡') : '✅';
    pointsLabel = f.hit ? `+${f.weight}` : '0';
    pointsColor = f.hit ? (f.weight >= 12 ? '#dc2626' : '#f59e0b') : '#10b981';
  }

  return (
    <div
      id={`factor-${f.key}`}
      style={{
        ...s.factorRow,
        opacity: f.hit ? 1 : 0.55,
        ...(f.hit && f.weight > 0 ? { background: severityColor(f.weight) + '12', borderLeft: `3px solid ${severityColor(f.weight)}`, paddingLeft: 8, fontWeight: 700 } : {}),
      }}
    >
      <span style={s.factorIcon}>{icon}</span>
      <span style={s.factorLabel}>{factorLabel(f, i18n)}</span>
      {detail && <span style={s.factorDetail}>{detail}</span>}
      <span style={{ ...s.factorPoints, color: pointsColor }}>{pointsLabel}</span>
    </div>
  );
}

const s = {
  page: { padding: '24px 32px', maxWidth: 900, margin: '0 auto' },
  header: { marginBottom: 12 },
  title: { margin: '0 0 4px', fontSize: 20, fontWeight: 800, color: 'var(--text)' },
  subtitle: { margin: 0, fontSize: 13, color: 'var(--text-muted)' },

  notice: {
    fontSize: 11, color: 'var(--text-muted)', background: 'var(--surface)',
    border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px',
    marginBottom: 16, lineHeight: 1.6,
  },

  form: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 },
  field: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 140 },
  fieldLabel: { fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 },
  input: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '9px 10px', fontSize: 13, color: 'var(--text)' },
  btnPrimary: { background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },

  errorBox: { background: '#dc262614', border: '1px solid #dc262655', color: '#dc2626', borderRadius: 8, padding: '10px 14px', fontSize: 12, marginBottom: 12 },

  resultCard: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '18px 20px', marginBottom: 20 },
  resultHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 },
  resultName: { fontSize: 16, fontWeight: 800, color: 'var(--text)' },
  resultSymbol: { fontWeight: 600, color: 'var(--text-muted)' },
  resultAddr: { fontSize: 11, color: 'var(--text-dim)', marginTop: 2, fontFamily: 'monospace' },
  badgeGroup: { display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' },
  scoreBadge: { fontSize: 12, fontWeight: 800, border: '1px solid', borderRadius: 10, padding: '5px 12px', whiteSpace: 'nowrap' },

  scoreBarsRow: { display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 10 },
  scoreBarCol: { flex: '1 1 200px', minWidth: 160 },
  scoreBarLabel: { fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 4 },
  scoreBarTrack: { height: 8, borderRadius: 4, background: 'var(--border)', overflow: 'hidden' },
  scoreBarFill: { height: '100%', transition: 'width 0.2s' },

  summaryRow: { display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 14 },

  notFoundBox: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '30px 20px', textAlign: 'center' },
  addressWarnBox: {
    fontSize: 12, color: '#991b1b', background: '#dc262614', border: '1px solid #dc262655',
    borderRadius: 8, padding: '10px 12px', marginBottom: 12, lineHeight: 1.5,
  },

  factorSection: { marginBottom: 14 },
  factorSectionTitle: { fontSize: 10.5, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 },
  factorRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 12.5, borderBottom: '1px solid var(--border)' },
  factorIcon: { fontSize: 12, flexShrink: 0 },
  factorLabel: { color: 'var(--text)', flex: '1 1 50%', minWidth: 0 },
  factorDetail: { color: 'var(--text-dim)', fontSize: 11, fontWeight: 700, flex: '0 1 45%', maxWidth: '45%', textAlign: 'right', overflowWrap: 'anywhere' },
  factorPoints: { fontSize: 11, fontWeight: 800, minWidth: 30, textAlign: 'right', flexShrink: 0 },

  findingsBox: { border: '1px solid #dc262640', background: '#dc26260a', borderRadius: 10, padding: '10px 12px', margin: '4px 0 14px', fontSize: 12.5, display: 'flex', flexDirection: 'column', gap: 4 },
  findingsTitle: { fontSize: 11, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', color: '#dc2626', marginBottom: 2 },
  findingItem: { display: 'flex', alignItems: 'flex-start', gap: 8, background: 'none', border: 'none', padding: '3px 0', textAlign: 'left', cursor: 'pointer', color: 'var(--text)', font: 'inherit' },
  findingPoints: { flexShrink: 0, minWidth: 34, textAlign: 'center', fontSize: 11, fontWeight: 800, border: '1px solid', borderRadius: 6, padding: '1px 4px' },
  findingText: { lineHeight: 1.45 },
  findingDetail: { color: 'var(--text-dim)', fontSize: 11.5 },
  disclaimer: { margin: '12px 0 0', fontSize: 10.5, lineHeight: 1.6, color: 'var(--text-dim)', fontStyle: 'italic' },

  recentSection: { marginTop: 4 },
  recentTitle: { fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 },
  recentList: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  recentChip: {
    display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start',
    background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10,
    padding: '7px 12px', cursor: 'pointer', minWidth: 90,
  },
  recentBadge: { fontSize: 10, fontWeight: 700, border: '1px solid', borderRadius: 6, padding: '1px 6px', marginTop: 2 },
};
